// What the funnel makes of the Add Video Source workflow's exit (ENG-3796).
//
// The funnel no longer draws its own discovery list or its own "which camera is
// this?" form. It opens the REAL Add Video Source modal and lets the shared
// panels do Discover, Authenticate, Configure video and Source details, then
// takes over where Connect controls would have been — with its own connection
// test and magic moment.
//
// This module is the seam. The shared panel hands over its own state
// (`OnboardingExit`) and knows nothing about onboarding; everything that turns
// that into the connection test's inputs is here, pure, and testable without a
// bridge, a room, or a renderer. That is the same split `discovery-selection.ts`
// existed to enforce, and the reason it can go: the rules it encoded about
// which sources are addable now live in the production selector, which is the
// component we are reusing.
//
// THE CONTROL ENDPOINT IS RESOLVED BY THE PANEL'S OWN CODE, not by a second
// interpretation of the template here. An earlier version of this file did
// interpret it, and produced `http://192.168.1.221:0` for a camera answering on
// `visca://192.168.1.221:1259?headers=false` — the exact class of invented
// endpoint the wizard was rebuilt to stop. See control/normalize.ts.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments, investigate-before-adding (ai.knowledge/global).

import type { Protocol as ControlProtocol } from '@studio/models/control';
import type { Model } from '@studio/models/device';
import type { Minimist } from '#~/device/index.js';
import type { OnboardingExit } from '#~/video/components/add/store/types.js';
// The enum lives in the store's types module, whose runtime graph is the enum
// and nothing else — importing it from the store barrel would drag the
// provider, the mediator and the device commands into every test that touches
// this file.
import { Panel } from '#~/video/components/add/store/types.js';
// THE PANEL'S OWN RESOLUTION, not a second interpretation of the template.
// Reimplementing it here is what sent control to `http://192.168.1.221:0` for a
// camera answering on `visca://192.168.1.221:1259?headers=false`. See
// video/components/add/control/normalize.ts.
import { resolveControlSettings } from '#~/video/components/add/control/normalize.js';
import type { OnboardingAddressSource } from './telemetry.js';
import type { FunnelScreenName } from './hooks/use-onboarding-tracker.js';

/** Everything the connection test needs, resolved from the workflow's exit. */
export interface ConnectionTestInput {
  /** Display identity: the title line and "three quick checks against <host>". */
  readonly name: string;
  readonly host: string;
  /**
   * The video configuration the Configure video step RESOLVED and proved.
   *
   * Passed to the add untouched. For an ONVIF camera this is the stream URI the
   * panel fetched from the device's capabilities
   * (`video/components/add/source/discovery.tsx`) — the address that cannot be
   * guessed, and the reason the funnel no longer runs an ONVIF probe of its own
   * on this route.
   */
  readonly video: unknown;
  /**
   * The control endpoint the Connect controls panel would have produced.
   *
   * Undefined when there is nothing to talk to, which hides the connection
   * test's control row rather than failing a check against an invented address.
   */
  readonly control: object | undefined;
  /** The chosen model, so the bridge resolves the same template we did. */
  readonly modelId: Model['id'] | undefined;
  /** The Bridge that will perform the add. */
  readonly agentId: string | undefined;
  /** Where the stream address came from. See telemetry.ts ADDRESS_RESOLVED. */
  readonly addressSource: OnboardingAddressSource | undefined;
  /** "PTZOptics Move 4K" style line under the checks, when the model is known. */
  readonly detected: string | undefined;
  /** The device the workflow's own commit created; the screen watches it. */
  readonly committedDeviceId: OnboardingExit['deviceId'];
}

/** A non-empty `host` on a candidate's config, or undefined. */
const readHost = (config: unknown): string | undefined => {
  if (typeof config !== 'object' || config === null || !('host' in config)) return undefined;

  const host = (config as { host?: unknown }).host;

  return typeof host === 'string' && host.trim() !== '' ? host.trim() : undefined;
};

/**
 * Where the resolved address came from.
 *
 * Three origins, and they fail differently — which is the whole reason
 * `onboarding_address_resolved` exists (telemetry.ts). A selection carrying its
 * own video configuration reported its address; a control-only (ONVIF)
 * selection had none until the panel asked the camera for it; no selection at
 * all means the user typed it.
 */
export const selectHandoffAddressSource = (
  selection: OnboardingExit['selection']
): OnboardingAddressSource | undefined => {
  if (selection === undefined) return 'form';
  if ('video' in selection && selection.video !== undefined) return 'discovery';
  if ('control' in selection && selection.control !== undefined) return 'onvif_probe';

  return undefined;
};

/**
 * Resolve the connection test's inputs from the workflow's exit.
 *
 * `name` mirrors what `wiring.handleCommit` would have saved — the candidate's
 * own name, falling back to the model's default — so the camera the funnel
 * reports and the camera a normal add would have created are the same camera.
 */
export const toConnectionTestInput = (exit: OnboardingExit): ConnectionTestInput => {
  const candidate = exit.candidate as {
    name?: string;
    video?: unknown;
    control?: { protocol?: ControlProtocol | undefined } | undefined;
    modelId?: Model['id'];
  };

  // The address the panel settled on. The candidate's video config is the
  // authority: for an ONVIF camera it holds the probed stream URI, which the
  // discovery record never had.
  const host = readHost(candidate.video) ?? readHost(candidate.control) ?? '';

  // The panel layers template -> the camera's own address -> app defaults, and
  // substitutes VISCA/IP for an ONVIF template because the app does not speak
  // ONVIF for control. Both rules were missing from the funnel's own version.
  const control = resolveControlSettings(candidate.control, exit.template, candidate.video);

  return {
    name: candidate.name?.trim() || exit.defaultName?.trim() || 'Camera',
    host,
    video: candidate.video,
    control,
    modelId: candidate.modelId,
    agentId: exit.agentId,
    addressSource: selectHandoffAddressSource(exit.selection),
    detected: exit.defaultName?.trim() || undefined,
    committedDeviceId: exit.deviceId
  };
};

/**
 * Which funnel screen a workflow panel IS, for reporting.
 *
 * The panels are the funnel's discovery, camera-form and camera-model screens
 * now, so they keep those names in ENG-4305's taxonomy rather than minting new
 * ones — the funnel's step positions are unchanged, only the components drawing
 * them are. `Panel.Control` has no entry because onboarding never reaches it;
 * that panel is exactly what the connection test replaces.
 */
export const funnelScreenForPanel = (panel: Panel): FunnelScreenName | undefined => {
  switch (panel) {
    case Panel.Source:
      return 'discovery';
    case Panel.Video:
      return 'manual-entry';
    case Panel.Manufacturer:
      return 'camera-model';
    default:
      return undefined;
  }
};

/** The candidate shape the connection test adds with. Re-exported for the gate. */
export type { Minimist };
